import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/dates';
import {
  Search,
  Loader2,
  Building2,
  MapPin,
  Home,
  CheckCircle2,
  AlertTriangle,
  User,
  Phone,
  Mail,
  FileText,
  Zap,
  ShieldCheck,
  CalendarClock,
  CreditCard,
  X,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  ArrowUpFromLine,
  Pencil,
  History,
} from 'lucide-react';
import { ACCOUNT_SERIES } from '@/lib/accountSeries';


interface CRMAccountResult {
  id: string;
  account_id: string;
  account_name: string;
  account_series_type: string | null;
  billed_status: string | null;
  account_category: string | null;
  is_active: boolean;
  contact_name: string | null;
  phone: string | null;
  email: string | null;
  default_project: string | null;
  default_cost_code: string | null;
  default_reference: string | null;
  open_approval: boolean;
  active_from: string | null;
  active_to: string | null;
  billing_name: string | null;
  billing_email: string | null;
  payment_terms: string | null;
  parent_account_id: string | null;
  traveller_full_name: string | null;
}

interface AccountAddress {
  id: string;
  address_type: string;
  label: string | null;
  full_address: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  suburb: string | null;
  city: string | null;
  postcode: string | null;
  is_active: boolean;
}

interface DcpJobContext {
  dcpId: string | number;
  taxicallerJobId: string;
  currentAccountId: string;
  currentPassenger: string;
  currentPickup: string;
  puDate: string;
  puTime: string;
  // Optional: callers can pass these if they already have them
  currentProject?: string | null;
  currentCostCode?: string | null;
  currentReference?: string | null;
}


interface AccountLookupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dcpJob: DcpJobContext | null;
  onAccountApplied?: (jobId: string, accountId: string, defaults: {
    project: string | null;
    costCode: string | null;
    reference: string | null;
  }) => void;
}

type BilledFilter = 'all' | 'billed' | 'non-billed';

const SELECT_COLS = 'id, account_id, account_name, account_series_type, billed_status, account_category, is_active, contact_name, phone, email, default_project, default_cost_code, default_reference, open_approval, active_from, active_to, billing_name, billing_email, payment_terms, parent_account_id, traveller_full_name';

const AccountLookupModal: React.FC<AccountLookupModalProps> = ({
  open,
  onOpenChange,
  dcpJob,
  onAccountApplied,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState<CRMAccountResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<CRMAccountResult | null>(null);
  const [addresses, setAddresses] = useState<AccountAddress[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [applying, setApplying] = useState(false);
  const [showAddresses, setShowAddresses] = useState(false);
  const [billedFilter, setBilledFilter] = useState<BilledFilter>('all');
  // Editable billing override fields
  const [overrideProject, setOverrideProject] = useState('');
  const [overrideCostCode, setOverrideCostCode] = useState('');
  const [overrideReference, setOverrideReference] = useState('');

  // Existing job billing values (fetched from taxicaller_data on open)
  const [existingJobBilling, setExistingJobBilling] = useState<{
    project: string;
    costCode: string;
    reference: string;
  }>({ project: '', costCode: '', reference: '' });

  // Parent account defaults (for inheritance)
  const [parentDefaults, setParentDefaults] = useState<{
    project: string | null;
    costCode: string | null;
    reference: string | null;
    parentName: string | null;
    parentId: string | null;
  } | null>(null);
  const [loadingParent, setLoadingParent] = useState(false);

  // Historical billing values from past jobs on this account
  const [historicalValues, setHistoricalValues] = useState<{
    projects: string[];
    costCodes: string[];
    references: string[];
  }>({ projects: [], costCodes: [], references: [] });
  const [loadingHistorical, setLoadingHistorical] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<'project' | 'costCode' | 'reference' | null>(null);


  // Fetch existing billing from taxicaller_data when modal opens
  const fetchExistingJobBilling = useCallback(async (jobId: string) => {
    try {
      const { data, error } = await supabase
        .from('taxicaller_data')
        .select('"Project", "Cost_Code", "Reference"')
        .eq('Job_ID', jobId)
        .limit(1)
        .maybeSingle();

      if (error) {
        console.warn('Failed to fetch existing billing:', error);
        return { project: '', costCode: '', reference: '' };
      }
      return {
        project: (data?.Project || '').trim(),
        costCode: (data?.Cost_Code || '').trim(),
        reference: (data?.Reference || '').trim(),
      };
    } catch (err) {
      console.warn('Error fetching existing billing:', err);
      return { project: '', costCode: '', reference: '' };
    }
  }, []);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open && dcpJob) {
      setSearchInput(dcpJob.currentAccountId || '');
      setSearchResults([]);
      setSelectedAccount(null);
      setAddresses([]);
      setShowAddresses(false);
      setParentDefaults(null);
      setBilledFilter('all');

      // Check if caller provided billing values directly
      const hasProvidedBilling = !!(
        dcpJob.currentProject?.trim() ||
        dcpJob.currentCostCode?.trim() ||
        dcpJob.currentReference?.trim()
      );

      if (hasProvidedBilling) {
        // Use caller-provided values
        const billing = {
          project: (dcpJob.currentProject || '').trim(),
          costCode: (dcpJob.currentCostCode || '').trim(),
          reference: (dcpJob.currentReference || '').trim(),
        };
        setExistingJobBilling(billing);
        setOverrideProject(billing.project);
        setOverrideCostCode(billing.costCode);
        setOverrideReference(billing.reference);
      } else {
        // Fetch from taxicaller_data
        setOverrideProject('');
        setOverrideCostCode('');
        setOverrideReference('');
        setExistingJobBilling({ project: '', costCode: '', reference: '' });

        if (dcpJob.taxicallerJobId) {
          fetchExistingJobBilling(dcpJob.taxicallerJobId).then((billing) => {
            setExistingJobBilling(billing);
            // Pre-populate override fields with existing job values
            setOverrideProject(billing.project);
            setOverrideCostCode(billing.costCode);
            setOverrideReference(billing.reference);
          });
        }
      }

      // Auto-search if we have an account ID
      if (dcpJob.currentAccountId) {
        searchAccounts(dcpJob.currentAccountId, 'all');
      }
    } else if (!open) {
      // Reset on close
      setExistingJobBilling({ project: '', costCode: '', reference: '' });
    }
  }, [open, dcpJob?.currentAccountId, dcpJob?.taxicallerJobId]);

  // When parent defaults load, backfill the override fields if they're still empty
  useEffect(() => {
    if (parentDefaults) {
      setOverrideProject(prev => prev || parentDefaults.project || '');
      setOverrideCostCode(prev => prev || parentDefaults.costCode || '');
      setOverrideReference(prev => prev || parentDefaults.reference || '');
    }
  }, [parentDefaults]);


  /**
   * Search CRM accounts with billed-first priority.
   */
  const searchAccounts = useCallback(async (query: string, filter: BilledFilter) => {
    if (!query.trim() || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const s = `%${query.trim()}%`;
      const orFilter = `account_id.ilike.${s},account_name.ilike.${s},contact_name.ilike.${s},traveller_full_name.ilike.${s}`;

      if (filter === 'all') {
        const [billedRes, nonBilledRes] = await Promise.all([
          supabase
            .from('crm_accounts')
            .select(SELECT_COLS)
            .or(orFilter)
            .eq('is_active', true)
            .eq('billed_status', 'Billed')
            .order('account_name', { ascending: true })
            .limit(20),
          supabase
            .from('crm_accounts')
            .select(SELECT_COLS)
            .or(orFilter)
            .eq('is_active', true)
            .eq('billed_status', 'Non-Billed')
            .order('account_name', { ascending: true })
            .limit(10),
        ]);

        if (billedRes.error) throw billedRes.error;
        if (nonBilledRes.error) throw nonBilledRes.error;

        const combined = [...(billedRes.data || []), ...(nonBilledRes.data || [])];
        setSearchResults(combined);
      } else {
        let q = supabase
          .from('crm_accounts')
          .select(SELECT_COLS)
          .or(orFilter)
          .eq('is_active', true);

        if (filter === 'billed') {
          q = q.eq('billed_status', 'Billed');
        } else {
          q = q.eq('billed_status', 'Non-Billed');
        }

        const { data, error } = await q
          .order('account_name', { ascending: true })
          .limit(30);

        if (error) throw error;
        setSearchResults(data || []);
      }
    } catch (err: any) {
      console.error('CRM search error:', err);
      toast({ title: 'Search Error', description: err.message || 'Failed to search accounts', variant: 'destructive' });
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      searchAccounts(searchInput, billedFilter);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput, open, searchAccounts, billedFilter]);

  // Load addresses when account selected
  const loadAddresses = useCallback(async (accountId: string) => {
    setLoadingAddresses(true);
    try {
      const { data, error } = await supabase
        .from('crm_account_addresses')
        .select('id, address_type, label, full_address, address_line_1, address_line_2, suburb, city, postcode, is_active')
        .eq('account_id', accountId)
        .order('address_type', { ascending: true })
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setAddresses(data || []);
    } catch (err) {
      console.error('Error loading addresses:', err);
      setAddresses([]);
    } finally {
      setLoadingAddresses(false);
    }
  }, []);

  // Load parent account defaults
  const loadParentDefaults = useCallback(async (parentAccountId: string) => {
    setLoadingParent(true);
    try {
      const { data, error } = await supabase
        .from('crm_accounts')
        .select('account_id, account_name, default_project, default_cost_code, default_reference')
        .eq('account_id', parentAccountId)
        .eq('is_active', true)
        .limit(1)
        .single();

      if (error) throw error;
      if (data) {
        setParentDefaults({
          project: data.default_project,
          costCode: data.default_cost_code,
          reference: data.default_reference,
          parentName: data.account_name,
          parentId: data.account_id,
        });
      }
    } catch (err) {
      console.error('Error loading parent defaults:', err);
      setParentDefaults(null);
    } finally {
      setLoadingParent(false);
    }
  }, []);

  // Load historical billing values (distinct Project, Cost_Code, Reference) for an account
  const loadHistoricalValues = useCallback(async (accountId: string) => {
    setLoadingHistorical(true);
    setHistoricalValues({ projects: [], costCodes: [], references: [] });
    setOpenDropdown(null);
    try {
      // Fetch recent jobs for this account with billing fields
      const { data, error } = await supabase
        .from('taxicaller_data')
        .select('"Project", "Cost_Code", "Reference"')
        .eq('Account_ID', accountId)
        .order('pu_date', { ascending: false, nullsFirst: false })
        .limit(500);

      if (error) throw error;

      // Extract distinct non-empty values
      const projectSet = new Set<string>();
      const costCodeSet = new Set<string>();
      const referenceSet = new Set<string>();

      (data || []).forEach((row: any) => {
        const p = (row.Project || '').trim();
        const c = (row.Cost_Code || '').trim();
        const r = (row.Reference || '').trim();
        if (p) projectSet.add(p);
        if (c) costCodeSet.add(c);
        if (r) referenceSet.add(r);
      });

      setHistoricalValues({
        projects: Array.from(projectSet).sort(),
        costCodes: Array.from(costCodeSet).sort(),
        references: Array.from(referenceSet).sort(),
      });
    } catch (err) {
      console.error('Error loading historical values:', err);
    } finally {
      setLoadingHistorical(false);
    }
  }, []);

  const handleSelectAccount = (account: CRMAccountResult) => {
    setSelectedAccount(account);
    setShowAddresses(false);
    setParentDefaults(null);
    setOpenDropdown(null);
    loadAddresses(account.account_id);
    loadHistoricalValues(account.account_id);

    // Account's own defaults
    const acctProject = account.default_project?.trim() || '';
    const acctCostCode = account.default_cost_code?.trim() || '';
    const acctReference = account.default_reference?.trim() || '';

    // Priority for each billing field:
    // 1. Existing job value (what's currently saved on the job — preserves manual overrides)
    // 2. Account's own default (if set)
    // 3. Empty (will try parent next if applicable)
    setOverrideProject(existingJobBilling.project || acctProject);
    setOverrideCostCode(existingJobBilling.costCode || acctCostCode);
    setOverrideReference(existingJobBilling.reference || acctReference);

    // If no billing defaults on the account itself, and it has a parent, fetch parent defaults
    const ownDefaults = !!(acctProject || acctCostCode || acctReference);
    if (!ownDefaults && account.parent_account_id) {
      loadParentDefaults(account.parent_account_id);
    }
  };





  // Determine the source label for each field
  const getFieldSource = (fieldValue: string, ownDefault: string | null | undefined, parentDefault: string | null | undefined) => {
    const own = ownDefault?.trim() || '';
    const parent = parentDefault || '';
    if (!fieldValue) return null;
    if (own && fieldValue === own) return 'default';
    if (parent && fieldValue === parent) return 'parent';
    return 'manual';
  };

  const handleApplyAccount = async () => {
    if (!selectedAccount || !dcpJob) return;
    setApplying(true);
    try {
      const finalProject = overrideProject.trim() || null;
      const finalCostCode = overrideCostCode.trim() || null;
      const finalReference = overrideReference.trim() || null;

      // Always write all three billing fields so overrides persist
      // (even writing empty/null clears old values that shouldn't remain)
      const updates: Record<string, any> = {
        Account_ID: selectedAccount.account_id,
        Project: finalProject || '',
        Cost_Code: finalCostCode || '',
        Reference: finalReference || '',
      };

      // Update taxicaller_data row
      const { error: tcError } = await supabase
        .from('taxicaller_data')
        .update(updates)
        .eq('Job_ID', dcpJob.taxicallerJobId);

      if (tcError) throw tcError;

      // Also update driver_completed_payments Cost_Code
      const dcpUpdates: Record<string, any> = {};
      if (finalCostCode !== undefined) dcpUpdates.Cost_Code = finalCostCode || '';
      if (Object.keys(dcpUpdates).length > 0) {
        const { error: dcpError } = await supabase
          .from('driver_completed_payments')
          .update(dcpUpdates)
          .eq('taxicaller_job_id', dcpJob.taxicallerJobId);

        if (dcpError) {
          console.warn('DCP Cost_Code update warning:', dcpError);
        }
      }

      // Build description of what was applied
      const filledFields = [
        finalProject && 'Project',
        finalCostCode && 'Cost Code',
        finalReference && 'Reference',
      ].filter(Boolean);

      toast({
        title: 'Account Applied',
        description: `Job ${dcpJob.taxicallerJobId} → A# ${selectedAccount.account_id}.${
          filledFields.length > 0 ? ` Billing fields backfilled.` : ''
        }`,
      });

      onAccountApplied?.(dcpJob.taxicallerJobId, selectedAccount.account_id, {
        project: finalProject,
        costCode: finalCostCode,
        reference: finalReference,
      });

      onOpenChange(false);
    } catch (err: any) {
      console.error('Apply account error:', err);
      toast({
        title: 'Update Failed',
        description: err.message || 'Failed to apply account to job',
        variant: 'destructive',
      });
    } finally {
      setApplying(false);
    }
  };


  const isExpired = (account: CRMAccountResult) => {
    if (!account.active_to) return false;
    return new Date(account.active_to) < new Date();
  };

  const hasDefaults = (account: CRMAccountResult) => {
    return !!(account.default_project?.trim() || account.default_cost_code?.trim() || account.default_reference?.trim());
  };

  const getSeriesColor = (series: string | null) => {
    return ACCOUNT_SERIES.find((s) => s.type === series)?.badgeClass || 'bg-secondary text-muted-foreground border-border';
  };

  const getBilledColor = (status: string | null) => {
    if (status === 'Billed') return 'bg-green-900/40 text-green-400 border-green-700/50';
    if (status === 'Non-Billed') return 'bg-neutral-800/60 text-neutral-400 border-neutral-600/50';
    return 'bg-secondary text-muted-foreground border-border';
  };

  const getFullAddress = (addr: AccountAddress) => {
    return addr.full_address || [addr.address_line_1, addr.address_line_2, addr.suburb, addr.city, addr.postcode].filter(Boolean).join(', ');
  };

  // Count billed vs non-billed in results
  const billedCount = searchResults.filter(a => a.billed_status === 'Billed').length;
  const nonBilledCount = searchResults.filter(a => a.billed_status !== 'Billed').length;

  // Check if any billing field has a value (for the apply button label)
  const hasAnyBillingValue = !!(overrideProject.trim() || overrideCostCode.trim() || overrideReference.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col bg-card border-border">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="golden-text text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Account Lookup
          </DialogTitle>
          {dcpJob && (
            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground flex-wrap">
              <span>Job: <span className="font-mono text-foreground">{dcpJob.taxicallerJobId}</span></span>
              <span className="text-border">|</span>
              <span>{dcpJob.puDate} {dcpJob.puTime}</span>
              {dcpJob.currentPassenger && (
                <>
                  <span className="text-border">|</span>
                  <span>{dcpJob.currentPassenger}</span>
                </>
              )}
              {dcpJob.currentAccountId && (
                <>
                  <span className="text-border">|</span>
                  <span>Current A#: <span className="font-mono text-foreground">{dcpJob.currentAccountId}</span></span>
                </>
              )}
            </div>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 py-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setSelectedAccount(null);
                setParentDefaults(null);
                // Restore existing job billing values when deselecting an account
                setOverrideProject(existingJobBilling.project);
                setOverrideCostCode(existingJobBilling.costCode);
                setOverrideReference(existingJobBilling.reference);
              }}
              className="dark-input pl-9"
              placeholder="Search by account number, name, traveller, or contact..."
              autoFocus
            />

            {searching && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
            )}
          </div>

          {/* Filter tabs */}
          {!selectedAccount && searchInput.length >= 2 && (
            <div className="flex items-center gap-1.5">
              {([
                { key: 'all' as BilledFilter, label: 'All', count: searchResults.length },
                { key: 'billed' as BilledFilter, label: 'Billed', count: billedFilter === 'billed' ? searchResults.length : billedCount },
                { key: 'non-billed' as BilledFilter, label: 'Non-Billed', count: billedFilter === 'non-billed' ? searchResults.length : nonBilledCount },
              ]).map(({ key, label, count }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setBilledFilter(key)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    billedFilter === key
                      ? key === 'billed'
                        ? 'bg-green-900/50 text-green-400 border border-green-700/60'
                        : key === 'non-billed'
                        ? 'bg-neutral-700/50 text-neutral-300 border border-neutral-600/60'
                        : 'bg-primary/20 text-primary border border-primary/40'
                      : 'bg-secondary/40 text-muted-foreground border border-border hover:bg-secondary/60'
                  }`}
                >
                  {label}
                  {count > 0 && <span className="ml-1 opacity-70">({count})</span>}
                </button>
              ))}
            </div>
          )}

          {/* Search Results (show when no account selected) */}
          {!selectedAccount && searchResults.length > 0 && (
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-secondary/30 border-b border-border">
                <p className="text-xs text-muted-foreground font-medium">
                  {searchResults.length} account{searchResults.length !== 1 ? 's' : ''} found
                  {billedFilter === 'all' && billedCount > 0 && (
                    <span className="ml-1 text-green-400">
                      ({billedCount} billed shown first)
                    </span>
                  )}
                </p>
              </div>
              <div className="max-h-[280px] overflow-y-auto">
                {searchResults.map((account, idx) => {
                  const prevAccount = idx > 0 ? searchResults[idx - 1] : null;
                  const showSeparator = billedFilter === 'all' &&
                    prevAccount?.billed_status === 'Billed' &&
                    account.billed_status !== 'Billed';

                  return (
                    <React.Fragment key={account.id}>
                      {showSeparator && (
                        <div className="px-3 py-1.5 bg-neutral-800/50 border-y border-border/50">
                          <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">
                            Non-Billed Accounts
                          </span>
                        </div>
                      )}
                      <button
                        type="button"
                        className={`w-full text-left px-3 py-2.5 hover:bg-secondary/40 transition-colors border-b border-border/50 last:border-b-0 ${
                          account.billed_status === 'Billed' ? 'bg-green-900/5' : ''
                        }`}
                        onClick={() => handleSelectAccount(account)}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-mono text-xs text-muted-foreground flex-shrink-0">{account.account_id}</span>
                            <span className="text-sm text-foreground font-medium truncate">{account.account_name}</span>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {account.account_series_type && (
                              <Badge className={`${getSeriesColor(account.account_series_type)} text-[10px] px-1.5 py-0`}>
                                {account.account_series_type}
                              </Badge>
                            )}
                            {account.billed_status && (
                              <Badge className={`${getBilledColor(account.billed_status)} text-[10px] px-1.5 py-0`}>
                                {account.billed_status === 'Billed' ? 'Billed' : 'Non-Billed'}
                              </Badge>
                            )}
                            {hasDefaults(account) ? (
                              <CheckCircle2 className="w-3.5 h-3.5 text-green-400" title="Has billing defaults" />
                            ) : (
                              <AlertTriangle className="w-3.5 h-3.5 text-yellow-400" title="No billing defaults" />
                            )}
                            {isExpired(account) && (
                              <Badge className="bg-red-900/50 text-red-400 border-red-700 text-[10px] py-0">Expired</Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          {account.contact_name && (
                            <p className="text-xs text-muted-foreground truncate">{account.contact_name}</p>
                          )}
                          {account.traveller_full_name && account.traveller_full_name !== account.account_name && (
                            <p className="text-xs text-blue-400/70 truncate">
                              Traveller: {account.traveller_full_name}
                            </p>
                          )}
                        </div>
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}

          {!selectedAccount && !searching && searchInput.length >= 2 && searchResults.length === 0 && (
            <div className="text-center py-6 text-muted-foreground">
              <Building2 className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No accounts found matching "{searchInput}"</p>
              {billedFilter !== 'all' && (
                <p className="text-xs mt-1">
                  Try switching to <button type="button" className="text-primary underline" onClick={() => setBilledFilter('all')}>All</button> to see more results.
                </p>
              )}
            </div>
          )}

          {/* Selected Account Details */}
          {selectedAccount && (
            <div className="space-y-3">
              {/* Account Header */}
              <Card className="bg-secondary/30 border-border">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Building2 className="w-4 h-4 text-primary" />
                        <span className="text-foreground font-semibold">{selectedAccount.account_name}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-muted-foreground">{selectedAccount.account_id}</span>
                        {selectedAccount.account_series_type && (
                          <Badge className={`${getSeriesColor(selectedAccount.account_series_type)} text-[10px] px-1.5 py-0`}>
                            {selectedAccount.account_series_type}
                          </Badge>
                        )}
                        {selectedAccount.billed_status && (
                          <Badge className={`${getBilledColor(selectedAccount.billed_status)} text-[10px] px-1.5 py-0`}>
                            {selectedAccount.billed_status}
                          </Badge>
                        )}
                        {selectedAccount.account_category && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{selectedAccount.account_category}</Badge>
                        )}
                        {selectedAccount.parent_account_id && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-blue-700/50 text-blue-400">
                            Parent: {selectedAccount.parent_account_id}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setSelectedAccount(null);
                        setParentDefaults(null);
                        // Restore existing job billing values when deselecting
                        setOverrideProject(existingJobBilling.project);
                        setOverrideCostCode(existingJobBilling.costCode);
                        setOverrideReference(existingJobBilling.reference);
                      }}

                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>

                  {/* Contact info */}
                  <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                    {selectedAccount.contact_name && (
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <User className="w-3 h-3" />
                        <span>{selectedAccount.contact_name}</span>
                      </div>
                    )}
                    {selectedAccount.traveller_full_name && (
                      <div className="flex items-center gap-1.5 text-blue-400/80">
                        <User className="w-3 h-3" />
                        <span>Traveller: {selectedAccount.traveller_full_name}</span>
                      </div>
                    )}
                    {selectedAccount.phone && (
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Phone className="w-3 h-3" />
                        <span>{selectedAccount.phone}</span>
                      </div>
                    )}
                    {selectedAccount.email && (
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Mail className="w-3 h-3" />
                        <span>{selectedAccount.email}</span>
                      </div>
                    )}
                    {selectedAccount.payment_terms && (
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <CreditCard className="w-3 h-3" />
                        <span>{selectedAccount.payment_terms}</span>
                      </div>
                    )}
                  </div>

                  {/* Active period */}
                  {(selectedAccount.active_from || selectedAccount.active_to) && (
                    <div className={`mt-3 pt-3 border-t ${isExpired(selectedAccount) ? 'border-red-800/40' : 'border-border'}`}>
                      <div className="flex items-center gap-2 text-xs">
                        <CalendarClock className={`w-3 h-3 ${isExpired(selectedAccount) ? 'text-red-400' : 'text-muted-foreground'}`} />
                        <span className={isExpired(selectedAccount) ? 'text-red-400 font-medium' : 'text-muted-foreground'}>
                          {selectedAccount.active_from ? formatDate(selectedAccount.active_from) : 'No start'} — {selectedAccount.active_to ? formatDate(selectedAccount.active_to) : 'No end'}
                        </span>
                        {isExpired(selectedAccount) && (
                          <Badge className="bg-red-900/50 text-red-400 border-red-700 text-[10px] py-0">Expired</Badge>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Editable Billing Fields */}
              {(() => {
                const ownProject = selectedAccount.default_project?.trim() || '';
                const ownCostCode = selectedAccount.default_cost_code?.trim() || '';
                const ownReference = selectedAccount.default_reference?.trim() || '';
                const hasOwnDefaults = !!(ownProject || ownCostCode || ownReference);
                const hasParentDefs = !!(parentDefaults?.project || parentDefaults?.costCode || parentDefaults?.reference);
                const isInherited = !hasOwnDefaults && hasParentDefs;

                // Determine card border color based on state
                const cardClass = hasAnyBillingValue
                  ? isInherited
                    ? 'bg-blue-900/10 border-blue-700/30'
                    : 'bg-green-900/10 border-green-700/30'
                  : 'bg-yellow-900/10 border-yellow-700/30';

                return (
                  <Card className={`border-border ${cardClass}`}>
                    <CardHeader className="pb-2 pt-3 px-4">
                      <CardTitle className="text-xs font-semibold uppercase tracking-wide flex items-center gap-2 text-muted-foreground">
                        <Pencil className="w-3.5 h-3.5" />
                        Billing Details (will be applied)
                        {loadingParent ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-400" />
                        ) : hasAnyBillingValue ? (
                          <CheckCircle2 className={`w-3.5 h-3.5 ${isInherited ? 'text-blue-400' : 'text-green-400'}`} />
                        ) : (
                          <AlertTriangle className="w-3.5 h-3.5 text-yellow-400" />
                        )}
                        {isInherited && (
                          <Badge className="bg-blue-900/40 text-blue-400 border-blue-700/50 text-[10px] px-1.5 py-0">
                            <ArrowUpFromLine className="w-2.5 h-2.5 mr-0.5" />
                            From Parent {parentDefaults?.parentId}
                          </Badge>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 pb-3">
                      <div className="grid grid-cols-3 gap-3">
                        {/* Project */}
                        <div className="relative">
                          <label className="text-muted-foreground text-xs block mb-1">
                            Project
                            {(() => {
                              const effectiveDefault = ownProject || parentDefaults?.project?.trim() || '';
                              if (!overrideProject) return null;
                              if (effectiveDefault && overrideProject === effectiveDefault) {
                                return <span className="ml-1 text-green-400 text-[10px]">({ownProject ? 'default' : 'parent'})</span>;
                              }
                              return <span className="ml-1 text-amber-400 text-[10px]">(override)</span>;
                            })()}
                          </label>
                          <div className="flex gap-1">
                            <Input
                              value={overrideProject}
                              onChange={(e) => setOverrideProject(e.target.value)}
                              className="dark-input h-8 text-sm flex-1"
                              placeholder="Enter project..."
                            />
                            {historicalValues.projects.length > 1 && (
                              <button
                                type="button"
                                className={`h-8 w-8 flex-shrink-0 flex items-center justify-center rounded-md border transition-colors ${
                                  openDropdown === 'project'
                                    ? 'bg-purple-900/40 border-purple-600/60 text-purple-400'
                                    : 'bg-secondary/50 border-border text-muted-foreground hover:text-foreground hover:border-purple-600/40'
                                }`}
                                onClick={() => setOpenDropdown(openDropdown === 'project' ? null : 'project')}
                                title={`${historicalValues.projects.length} historical values`}
                              >
                                <History className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          {openDropdown === 'project' && historicalValues.projects.length > 1 && (
                            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-card border border-purple-700/40 rounded-lg shadow-lg overflow-hidden">
                              <div className="px-2 py-1.5 bg-purple-900/20 border-b border-purple-700/30">
                                <p className="text-[10px] text-purple-400 font-semibold uppercase tracking-wide flex items-center gap-1">
                                  <History className="w-3 h-3" />
                                  {historicalValues.projects.length} historical values
                                </p>
                              </div>
                              <div className="max-h-[140px] overflow-y-auto">
                                {historicalValues.projects.map((val) => (
                                  <button
                                    key={val}
                                    type="button"
                                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-secondary/40 transition-colors border-b border-border/30 last:border-b-0 ${
                                      overrideProject === val ? 'bg-purple-900/20 text-purple-300 font-medium' : 'text-foreground'
                                    }`}
                                    onClick={() => { setOverrideProject(val); setOpenDropdown(null); }}
                                  >
                                    {val}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          {(() => {
                            const effectiveDefault = ownProject || parentDefaults?.project?.trim() || '';
                            const source = ownProject ? 'default' : parentDefaults?.project ? 'parent' : '';
                            if (effectiveDefault && overrideProject && overrideProject !== effectiveDefault) {
                              return (
                                <p className="text-[10px] text-neutral-500 mt-1 truncate" title={effectiveDefault}>
                                  {source === 'parent' ? 'Parent default' : 'Default'}: {effectiveDefault}
                                </p>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        {/* Cost Code */}
                        <div className="relative">
                          <label className="text-muted-foreground text-xs block mb-1">
                            Cost Code
                            {(() => {
                              const effectiveDefault = ownCostCode || parentDefaults?.costCode?.trim() || '';
                              if (!overrideCostCode) return null;
                              if (effectiveDefault && overrideCostCode === effectiveDefault) {
                                return <span className="ml-1 text-green-400 text-[10px]">({ownCostCode ? 'default' : 'parent'})</span>;
                              }
                              return <span className="ml-1 text-amber-400 text-[10px]">(override)</span>;
                            })()}
                          </label>
                          <div className="flex gap-1">
                            <Input
                              value={overrideCostCode}
                              onChange={(e) => setOverrideCostCode(e.target.value)}
                              className="dark-input h-8 text-sm flex-1"
                              placeholder="Enter cost code..."
                            />
                            {historicalValues.costCodes.length > 1 && (
                              <button
                                type="button"
                                className={`h-8 w-8 flex-shrink-0 flex items-center justify-center rounded-md border transition-colors ${
                                  openDropdown === 'costCode'
                                    ? 'bg-purple-900/40 border-purple-600/60 text-purple-400'
                                    : 'bg-secondary/50 border-border text-muted-foreground hover:text-foreground hover:border-purple-600/40'
                                }`}
                                onClick={() => setOpenDropdown(openDropdown === 'costCode' ? null : 'costCode')}
                                title={`${historicalValues.costCodes.length} historical values`}
                              >
                                <History className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          {openDropdown === 'costCode' && historicalValues.costCodes.length > 1 && (
                            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-card border border-purple-700/40 rounded-lg shadow-lg overflow-hidden">
                              <div className="px-2 py-1.5 bg-purple-900/20 border-b border-purple-700/30">
                                <p className="text-[10px] text-purple-400 font-semibold uppercase tracking-wide flex items-center gap-1">
                                  <History className="w-3 h-3" />
                                  {historicalValues.costCodes.length} historical values
                                </p>
                              </div>
                              <div className="max-h-[140px] overflow-y-auto">
                                {historicalValues.costCodes.map((val) => (
                                  <button
                                    key={val}
                                    type="button"
                                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-secondary/40 transition-colors border-b border-border/30 last:border-b-0 ${
                                      overrideCostCode === val ? 'bg-purple-900/20 text-purple-300 font-medium' : 'text-foreground'
                                    }`}
                                    onClick={() => { setOverrideCostCode(val); setOpenDropdown(null); }}
                                  >
                                    {val}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          {(() => {
                            const effectiveDefault = ownCostCode || parentDefaults?.costCode?.trim() || '';
                            const source = ownCostCode ? 'default' : parentDefaults?.costCode ? 'parent' : '';
                            if (effectiveDefault && overrideCostCode && overrideCostCode !== effectiveDefault) {
                              return (
                                <p className="text-[10px] text-neutral-500 mt-1 truncate" title={effectiveDefault}>
                                  {source === 'parent' ? 'Parent default' : 'Default'}: {effectiveDefault}
                                </p>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        {/* Reference */}
                        <div className="relative">
                          <label className="text-muted-foreground text-xs block mb-1">
                            Reference
                            {(() => {
                              const effectiveDefault = ownReference || parentDefaults?.reference?.trim() || '';
                              if (!overrideReference) return null;
                              if (effectiveDefault && overrideReference === effectiveDefault) {
                                return <span className="ml-1 text-green-400 text-[10px]">({ownReference ? 'default' : 'parent'})</span>;
                              }
                              return <span className="ml-1 text-amber-400 text-[10px]">(override)</span>;
                            })()}
                          </label>
                          <div className="flex gap-1">
                            <Input
                              value={overrideReference}
                              onChange={(e) => setOverrideReference(e.target.value)}
                              className="dark-input h-8 text-sm flex-1"
                              placeholder="Enter reference..."
                            />
                            {historicalValues.references.length > 1 && (
                              <button
                                type="button"
                                className={`h-8 w-8 flex-shrink-0 flex items-center justify-center rounded-md border transition-colors ${
                                  openDropdown === 'reference'
                                    ? 'bg-purple-900/40 border-purple-600/60 text-purple-400'
                                    : 'bg-secondary/50 border-border text-muted-foreground hover:text-foreground hover:border-purple-600/40'
                                }`}
                                onClick={() => setOpenDropdown(openDropdown === 'reference' ? null : 'reference')}
                                title={`${historicalValues.references.length} historical values`}
                              >
                                <History className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          {openDropdown === 'reference' && historicalValues.references.length > 1 && (
                            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-card border border-purple-700/40 rounded-lg shadow-lg overflow-hidden">
                              <div className="px-2 py-1.5 bg-purple-900/20 border-b border-purple-700/30">
                                <p className="text-[10px] text-purple-400 font-semibold uppercase tracking-wide flex items-center gap-1">
                                  <History className="w-3 h-3" />
                                  {historicalValues.references.length} historical values
                                </p>
                              </div>
                              <div className="max-h-[140px] overflow-y-auto">
                                {historicalValues.references.map((val) => (
                                  <button
                                    key={val}
                                    type="button"
                                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-secondary/40 transition-colors border-b border-border/30 last:border-b-0 ${
                                      overrideReference === val ? 'bg-purple-900/20 text-purple-300 font-medium' : 'text-foreground'
                                    }`}
                                    onClick={() => { setOverrideReference(val); setOpenDropdown(null); }}
                                  >
                                    {val}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          {(() => {
                            const effectiveDefault = ownReference || parentDefaults?.reference?.trim() || '';
                            const source = ownReference ? 'default' : parentDefaults?.reference ? 'parent' : '';
                            if (effectiveDefault && overrideReference && overrideReference !== effectiveDefault) {
                              return (
                                <p className="text-[10px] text-neutral-500 mt-1 truncate" title={effectiveDefault}>
                                  {source === 'parent' ? 'Parent default' : 'Default'}: {effectiveDefault}
                                </p>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>

                      {/* Historical values indicator */}
                      {!loadingHistorical && (historicalValues.projects.length > 1 || historicalValues.costCodes.length > 1 || historicalValues.references.length > 1) && (
                        <p className="text-[10px] text-purple-400/70 mt-2 flex items-center gap-1">
                          <History className="w-3 h-3 flex-shrink-0" />
                          Multiple historical values found — click the clock icon to choose from previously used values.
                        </p>
                      )}

                      {/* Contextual help text */}
                      {(() => {
                        // Check if any field is overridden (differs from its default)
                        const projDefault = ownProject || parentDefaults?.project?.trim() || '';
                        const ccDefault = ownCostCode || parentDefaults?.costCode?.trim() || '';
                        const refDefault = ownReference || parentDefaults?.reference?.trim() || '';
                        const anyOverridden = (overrideProject && projDefault && overrideProject !== projDefault) ||
                          (overrideCostCode && ccDefault && overrideCostCode !== ccDefault) ||
                          (overrideReference && refDefault && overrideReference !== refDefault);

                        if (!hasAnyBillingValue && !loadingParent) {
                          return (
                            <p className="text-xs text-yellow-400 mt-2.5 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                              No billing defaults found{selectedAccount.parent_account_id ? ' (checked parent too)' : ''}. You can type values above to set them for this job.
                            </p>
                          );
                        }
                        if (anyOverridden) {
                          return (
                            <p className="text-xs text-amber-400/70 mt-2.5 flex items-center gap-1">
                              <Pencil className="w-3 h-3 flex-shrink-0" />
                              Custom values will be applied. Account defaults shown in grey below each field.
                            </p>
                          );
                        }
                        if (hasAnyBillingValue && isInherited && parentDefaults?.parentName) {
                          return (
                            <p className="text-xs text-blue-400 mt-2.5 flex items-center gap-1">
                              <ArrowUpFromLine className="w-3 h-3 flex-shrink-0" />
                              Pre-filled from parent: {parentDefaults.parentName} ({parentDefaults.parentId}). Edit above to override.
                            </p>
                          );
                        }
                        if (hasOwnDefaults) {
                          return (
                            <p className="text-xs text-green-400/70 mt-2.5 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
                              Pre-filled from account defaults. Edit above to override for this job.
                            </p>
                          );
                        }
                        return null;
                      })()}
                    </CardContent>
                  </Card>

                );
              })()}


              {/* Approved Addresses - Collapsible */}
              <Card className="bg-secondary/30 border-border">
                <CardHeader className="pb-0 pt-3 px-4">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between py-1 group"
                    onClick={() => setShowAddresses(!showAddresses)}
                  >
                    <CardTitle className="text-xs font-semibold uppercase tracking-wide flex items-center gap-2 text-muted-foreground">
                      <MapPin className="w-3.5 h-3.5" />
                      Approved Addresses
                      {selectedAccount.open_approval && (
                        <Badge className="bg-green-900/50 text-green-400 border-green-700 text-[10px] py-0">
                          <ShieldCheck className="w-2.5 h-2.5 mr-0.5" />
                          Open Approval
                        </Badge>
                      )}
                      <Badge variant="secondary" className="text-[10px] px-1 py-0">{addresses.length}</Badge>
                    </CardTitle>
                    {showAddresses ? (
                      <ChevronUp className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                    )}
                  </button>
                </CardHeader>
                {showAddresses && (
                  <CardContent className="px-4 pb-3 pt-2">
                    {loadingAddresses ? (
                      <div className="flex items-center justify-center py-4 text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        <span className="text-xs">Loading addresses...</span>
                      </div>
                    ) : selectedAccount.open_approval ? (
                      <div className="flex items-center gap-2 py-3 px-3 rounded-lg border border-green-700/40 bg-green-900/10">
                        <ShieldCheck className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <p className="text-xs text-green-400">
                          Open approval enabled — this account can travel to any destination.
                        </p>
                      </div>
                    ) : addresses.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-3">
                        No approved addresses configured.
                      </p>
                    ) : (
                      <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                        {addresses.map((addr) => {
                          const isHome = addr.address_type === 'home';
                          const fullAddr = getFullAddress(addr);
                          return (
                            <div
                              key={addr.id}
                              className={`flex items-start gap-2 rounded-lg border p-2 ${
                                isHome ? 'border-blue-700/40 bg-blue-900/10' : 'border-border bg-secondary/20'
                              } ${!addr.is_active ? 'opacity-50' : ''}`}
                            >
                              {isHome ? (
                                <Home className="w-3 h-3 text-blue-400 mt-0.5 flex-shrink-0" />
                              ) : (
                                <MapPin className="w-3 h-3 text-muted-foreground mt-0.5 flex-shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <span className={`text-[11px] font-medium ${isHome ? 'text-blue-400' : 'text-foreground'}`}>
                                  {isHome ? 'Home' : (addr.label || 'Destination')}
                                </span>
                                <p className="text-[11px] text-foreground/80 truncate">{fullAddr || 'No address details'}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 gap-2 border-t border-border pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-border text-muted-foreground"
          >
            Cancel
          </Button>
          {selectedAccount && (
            <Button
              onClick={handleApplyAccount}
              disabled={applying}
              className="golden-button"
            >
              {applying ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <ArrowRight className="w-4 h-4 mr-1.5" />
              )}
              Apply {selectedAccount.account_id} to Job
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AccountLookupModal;
